import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Trophy, Flag, Search, Plus, Trash2, Loader2, Globe,
  ChevronRight, ChevronDown, Shield, ToggleLeft, Activity,
  CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function SportsManagePage() {
  const [tab, setTab] = useState('managed');
  const [managedLeagues, setManagedLeagues] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryLeagues, setCountryLeagues] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [expandedCountries, setExpandedCountries] = useState({});
  const [managedSearch, setManagedSearch] = useState('');

  const loadManaged = useCallback(async () => {
    try {
      const r = await api.get('/sports/managed-leagues');
      setManagedLeagues(r.data);
    } catch { }
  }, []);

  useEffect(() => { loadManaged(); }, [loadManaged]);

  const loadCountries = async () => {
    if (countries.length > 0) return;
    setLoadingCountries(true);
    try {
      const r = await api.get('/sports/countries');
      setCountries(r.data);
    } catch {
      toast.error('Ülkeler yüklenemedi. API-Football anahtarını kontrol edin.');
    }
    setLoadingCountries(false);
  };

  const loadLeaguesForCountry = async (country) => {
    setSelectedCountry(country);
    setLoadingLeagues(true);
    setCountryLeagues([]);
    try {
      const r = await api.get('/sports/leagues', { params: { country: country.name } });
      setCountryLeagues(r.data);
    } catch {
      toast.error('Ligler yüklenemedi');
    }
    setLoadingLeagues(false);
  };

  const addLeague = async (league) => {
    setAddingId(league.id);
    try {
      await api.post('/sports/managed-leagues', {
        league_id: league.id, name: league.name, logo: league.logo,
        country: league.country, flag: league.flag,
      });
      toast.success(`${league.name} eklendi`);
      await loadManaged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    }
    setAddingId(null);
  };

  const toggleLeague = async (leagueId) => {
    setTogglingId(leagueId);
    try {
      const r = await api.put(`/sports/managed-leagues/${leagueId}/toggle`);
      setManagedLeagues(prev => prev.map(l =>
        l.league_id === leagueId ? { ...l, is_active: r.data.is_active } : l
      ));
      toast.success(r.data.is_active ? 'Lig aktifleştirildi' : 'Lig pasifleştirildi');
    } catch {
      toast.error('Değiştirilemedi');
    }
    setTogglingId(null);
  };

  const removeLeague = async (leagueId, name) => {
    try {
      await api.delete(`/sports/managed-leagues/${leagueId}`);
      setManagedLeagues(prev => prev.filter(l => l.league_id !== leagueId));
      toast.success(`${name} kaldırıldı`);
    } catch {
      toast.error('Kaldırılamadı');
    }
  };

  // Group managed leagues by country
  const groupedByCountry = useMemo(() => {
    const groups = {};
    const filtered = managedSearch
      ? managedLeagues.filter(l =>
          l.name.toLowerCase().includes(managedSearch.toLowerCase()) ||
          l.country?.toLowerCase().includes(managedSearch.toLowerCase())
        )
      : managedLeagues;

    filtered.forEach(l => {
      const c = l.country || 'Diğer';
      if (!groups[c]) groups[c] = { flag: l.flag, leagues: [] };
      groups[c].leagues.push(l);
    });

    return Object.entries(groups).sort((a, b) => {
      const aActive = a[1].leagues.filter(l => l.is_active).length;
      const bActive = b[1].leagues.filter(l => l.is_active).length;
      return bActive - aActive || b[1].leagues.length - a[1].leagues.length;
    });
  }, [managedLeagues, managedSearch]);

  // Stats
  const stats = useMemo(() => ({
    total: managedLeagues.length,
    active: managedLeagues.filter(l => l.is_active).length,
    inactive: managedLeagues.filter(l => !l.is_active).length,
    countries: [...new Set(managedLeagues.map(l => l.country))].length,
  }), [managedLeagues]);

  const toggleCountryExpand = (country) => {
    setExpandedCountries(prev => ({ ...prev, [country]: !prev[country] }));
  };

  // Auto-expand all countries initially
  useEffect(() => {
    if (managedLeagues.length > 0) {
      const expanded = {};
      managedLeagues.forEach(l => { expanded[l.country || 'Diger'] = true; });
      setExpandedCountries(expanded);
    }
  }, [managedLeagues]);

  const toggleAllInCountry = async (countryLeaguesList, targetActive) => {
    const toToggle = countryLeaguesList.filter(l => l.is_active !== targetActive);
    for (const l of toToggle) {
      await toggleLeague(l.league_id);
    }
  };

  const managedIds = new Set(managedLeagues.map(l => l.league_id));
  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  return (
    <div data-testid="sports-manage-page" className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="pb-6 border-b border-[#262626]">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-500/20 flex items-center justify-center">
            <Trophy size={20} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Spor Yönetimi</h1>
            <p className="text-[#A3A3A3] font-ibm text-sm mt-0.5">Takip edilecek ligleri ve maçları yönetin</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Toplam Lig', value: stats.total, icon: Trophy, color: 'text-white', bg: 'bg-white/5 border-[#262626]' },
          { label: 'Aktif Lig', value: stats.active, icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/5 border-emerald-500/20' },
          { label: 'Pasif Lig', value: stats.inactive, icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/20' },
          { label: 'Ülke', value: stats.countries, icon: Globe, color: 'text-blue-400', bg: 'bg-blue-500/5 border-blue-500/20' },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl border p-4 ${s.bg} transition-all duration-300`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between">
              <s.icon size={18} className={`${s.color} opacity-60`} />
              <span className={`text-2xl font-outfit font-bold ${s.color}`}>{s.value}</span>
            </div>
            <p className="text-xs text-[#737373] font-ibm mt-2">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'add') loadCountries(); }}>
        <TabsList className="bg-[#141414] border border-[#262626] p-1 h-auto">
          <TabsTrigger value="managed" className="text-sm font-ibm data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-managed">
            <Shield size={14} className="mr-1.5" /> Lig Yönetimi ({managedLeagues.length})
          </TabsTrigger>
          <TabsTrigger value="add" className="text-sm font-ibm data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-add">
            <Plus size={14} className="mr-1.5" /> Lig Ekle
          </TabsTrigger>
        </TabsList>

        {/* ═══════ MANAGED LEAGUES TAB ═══════ */}
        <TabsContent value="managed" className="mt-6">
          {managedLeagues.length === 0 ? (
            <Card className="bg-[#141414] border-[#262626]">
              <CardContent className="py-16 text-center">
                <Trophy size={40} className="mx-auto text-[#262626] mb-4" />
                <p className="text-[#737373] font-ibm text-sm">Henüz lig eklenmedi</p>
                <p className="text-[#525252] font-ibm text-xs mt-1">Lig Ekle sekmesinden takip etmek istediğiniz ligleri seçin</p>
                <Button onClick={() => { setTab('add'); loadCountries(); }} className="mt-4 bg-emerald-600 hover:bg-emerald-500 text-white text-sm" data-testid="goto-add-btn">
                  <Plus size={14} className="mr-1" /> İlk Ligi Ekle
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
                <Input
                  value={managedSearch}
                  onChange={e => setManagedSearch(e.target.value)}
                  placeholder="Lig veya ülke ara..."
                  className="bg-[#141414] border-[#262626] text-white pl-9 h-9 text-sm"
                  data-testid="managed-search"
                />
              </div>

              {/* Country Groups */}
              <div className="space-y-3">
                {groupedByCountry.map(([country, data]) => {
                  const isExpanded = expandedCountries[country] !== false;
                  const activeCount = data.leagues.filter(l => l.is_active).length;
                  const totalCount = data.leagues.length;
                  const allActive = activeCount === totalCount;
                  const allInactive = activeCount === 0;

                  return (
                    <div key={country} className="rounded-xl border border-[#262626] overflow-hidden bg-[#141414] transition-all duration-300"
                      data-testid={`country-group-${country}`}>
                      {/* Country Header */}
                      <button
                        onClick={() => toggleCountryExpand(country)}
                        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.02] transition-colors duration-200"
                        data-testid={`country-toggle-${country}`}
                      >
                        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
                          <ChevronDown size={16} className="text-[#525252]" />
                        </div>
                        {data.flag ? (
                          <img src={data.flag} alt="" className="w-6 h-4 object-cover rounded-sm shadow-sm" />
                        ) : (
                          <Flag size={16} className="text-[#525252]" />
                        )}
                        <span className="text-sm font-ibm text-white font-medium flex-1 text-left">{country}</span>

                        {/* Country Stats Badges */}
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-[10px] font-jetbrains px-2 py-0 ${
                            allActive
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : allInactive
                                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                          }`}>
                            {activeCount}/{totalCount} aktif
                          </Badge>
                        </div>
                      </button>

                      {/* Leagues under country */}
                      {isExpanded && (
                        <div className="border-t border-[#1A1A1A]">
                          {/* Bulk toggle */}
                          {totalCount > 1 && (
                            <div className="flex items-center justify-between px-4 py-2 bg-[#0D0D0D] border-b border-[#1A1A1A]">
                              <span className="text-[10px] text-[#525252] font-ibm uppercase tracking-wider">Toplu İşlem</span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleAllInCountry(data.leagues, true); }}
                                  className="text-[10px] text-emerald-400/70 hover:text-emerald-400 font-ibm px-2 py-0.5 rounded hover:bg-emerald-500/10 transition-colors"
                                  data-testid={`activate-all-${country}`}
                                >
                                  Tümünü Aç
                                </button>
                                <span className="text-[#262626]">|</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleAllInCountry(data.leagues, false); }}
                                  className="text-[10px] text-red-400/70 hover:text-red-400 font-ibm px-2 py-0.5 rounded hover:bg-red-500/10 transition-colors"
                                  data-testid={`deactivate-all-${country}`}
                                >
                                  Tümünü Kapat
                                </button>
                              </div>
                            </div>
                          )}

                          {data.leagues.map((league, idx) => (
                            <div
                              key={league.league_id}
                              className={`flex items-center gap-3 px-4 py-3 transition-all duration-200 group ${
                                idx < data.leagues.length - 1 ? 'border-b border-[#1A1A1A]' : ''
                              } ${league.is_active ? 'bg-[#141414]' : 'bg-[#0D0D0D]'}`}
                              data-testid={`managed-league-${league.league_id}`}
                            >
                              {/* League Logo */}
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                                league.is_active
                                  ? 'bg-white/5 border border-white/10'
                                  : 'bg-[#0A0A0A] border border-[#1A1A1A] opacity-50'
                              }`}>
                                {league.logo ? (
                                  <img src={league.logo} alt="" className="w-6 h-6 object-contain" />
                                ) : (
                                  <Trophy size={16} className="text-[#525252]" />
                                )}
                              </div>

                              {/* League Info */}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-ibm font-medium truncate transition-colors duration-200 ${
                                  league.is_active ? 'text-white' : 'text-[#525252]'
                                }`}>{league.name}</p>
                                <span className="text-[10px] text-[#525252] font-jetbrains">ID: {league.league_id}</span>
                              </div>

                              {/* Status Badge */}
                              <Badge variant="outline" className={`text-[10px] shrink-0 transition-all duration-200 ${
                                league.is_active
                                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                  : 'bg-red-500/10 text-red-400 border-red-500/20'
                              }`}>
                                {league.is_active ? 'Aktif' : 'Pasif'}
                              </Badge>

                              {/* Toggle Switch */}
                              <Switch
                                checked={league.is_active}
                                onCheckedChange={() => toggleLeague(league.league_id)}
                                disabled={togglingId === league.league_id}
                                className="data-[state=checked]:bg-emerald-500"
                                data-testid={`toggle-league-${league.league_id}`}
                              />

                              {/* Remove Button */}
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => removeLeague(league.league_id, league.name)}
                                className="h-8 w-8 p-0 text-[#525252] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                data-testid={`remove-league-${league.league_id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {groupedByCountry.length === 0 && managedSearch && (
                <div className="text-center py-8">
                  <Search size={24} className="mx-auto text-[#262626] mb-3" />
                  <p className="text-[#525252] font-ibm text-sm">"{managedSearch}" ile eşleşen lig bulunamadı</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ═══════ ADD LEAGUES TAB ═══════ */}
        <TabsContent value="add" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Country List */}
            <Card className="bg-[#141414] border-[#262626]">
              <div className="p-4 border-b border-[#262626]">
                <div className="flex items-center gap-2 mb-3">
                  <Globe size={16} className="text-blue-400" />
                  <span className="font-outfit text-base text-white font-medium">Ülke Seçin</span>
                </div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
                  <Input
                    value={countrySearch}
                    onChange={e => setCountrySearch(e.target.value)}
                    placeholder="Ülke ara..."
                    className="bg-[#0A0A0A] border-[#262626] text-white pl-9 h-9 text-sm"
                    data-testid="country-search"
                  />
                </div>
              </div>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-1 p-3">
                {loadingCountries ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-emerald-400" />
                    <span className="ml-2 text-sm text-[#737373] font-ibm">Ülkeler yükleniyor...</span>
                  </div>
                ) : filteredCountries.length === 0 ? (
                  <p className="text-[#525252] font-ibm text-sm py-4 text-center">
                    {countries.length === 0 ? 'Ülkeler yüklenemedi' : 'Sonuç bulunamadı'}
                  </p>
                ) : (
                  filteredCountries.map(country => (
                    <button key={country.name} onClick={() => loadLeaguesForCountry(country)}
                      data-testid={`country-${country.code || country.name}`}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                        selectedCountry?.name === country.name
                          ? 'bg-emerald-500/10 border border-emerald-500/20'
                          : 'hover:bg-white/5 border border-transparent'
                      }`}>
                      <div className="flex items-center gap-2.5">
                        {country.flag ? (
                          <img src={country.flag} alt="" className="w-5 h-3.5 object-cover rounded-sm" />
                        ) : (
                          <Flag size={14} className="text-[#525252]" />
                        )}
                        <span className="text-sm text-white font-ibm">{country.name}</span>
                      </div>
                      <ChevronRight size={14} className="text-[#525252]" />
                    </button>
                  ))
                )}
              </CardContent>
            </Card>

            {/* League List for Selected Country */}
            <Card className="bg-[#141414] border-[#262626]">
              <div className="p-4 border-b border-[#262626]">
                <div className="flex items-center gap-2">
                  <Trophy size={16} className="text-amber-400" />
                  <span className="font-outfit text-base text-white font-medium">
                    {selectedCountry ? `${selectedCountry.name} Ligleri` : 'Ligler'}
                  </span>
                </div>
              </div>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-2 p-3">
                {!selectedCountry ? (
                  <p className="text-[#525252] font-ibm text-sm py-8 text-center">Bir ülke seçin</p>
                ) : loadingLeagues ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-emerald-400" />
                    <span className="ml-2 text-sm text-[#737373] font-ibm">Ligler yükleniyor...</span>
                  </div>
                ) : countryLeagues.length === 0 ? (
                  <p className="text-[#525252] font-ibm text-sm py-8 text-center">Bu ülkede lig bulunamadı</p>
                ) : (
                  countryLeagues.map(league => {
                    const isManaged = managedIds.has(league.id);
                    return (
                      <div key={league.id}
                        data-testid={`league-${league.id}`}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          isManaged ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-[#0A0A0A] border-[#1A1A1A] hover:border-[#262626]'
                        }`}>
                        <div className="flex items-center gap-3">
                          {league.logo ? (
                            <img src={league.logo} alt="" className="w-8 h-8 object-contain" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-[#262626] flex items-center justify-center">
                              <Trophy size={14} className="text-[#525252]" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm text-white font-ibm">{league.name}</p>
                            <span className="text-[10px] text-[#525252] font-jetbrains">ID: {league.id}</span>
                          </div>
                        </div>
                        {isManaged ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Eklendi</Badge>
                        ) : (
                          <Button size="sm" onClick={() => addLeague(league)} disabled={addingId === league.id}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs px-3"
                            data-testid={`add-league-${league.id}`}>
                            {addingId === league.id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} className="mr-1" />}
                            Ekle
                          </Button>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
